'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/field';
import { AuthBrandHeader } from './auth-brand-header';
import {
  forgotPasswordSchema,
  type ForgotPasswordInput,
} from '@/lib/schemas/auth/password-reset.schema';
import { useRequestPasswordReset } from '@/hooks/mutations/auth/use-request-password-reset';
import { mapAuthError } from '@/lib/auth/auth-errors';
import { useFieldError } from '@/lib/i18n/use-field-error';

export function AuthForgotPasswordFeature() {
  const t = useTranslations('auth');
  const fieldError = useFieldError('auth');
  const router = useRouter();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const form = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });
  const mutation = useRequestPasswordReset();

  const onSubmit = form.handleSubmit((values) => {
    mutation.mutate(values, {
      onSuccess: () => setSentTo(values.email),
      onError: (err) => {
        const key = mapAuthError(err);
        toast.error(t(`errors.${key ?? 'fallbackForgotPassword'}`));
      },
    });
  });

  if (sentTo) {
    return (
      <div className="flex flex-col gap-5">
        <AuthBrandHeader title={t('forgotPassword.sentTitle')} />
        <p className="text-mindexia-muted text-center text-sm">
          {t.rich('forgotPassword.sentBody', {
            email: sentTo,
            strong: (chunks) => <span className="text-foreground font-medium">{chunks}</span>,
          })}
        </p>
        <Button
          type="button"
          variant="mindexiaOutline"
          size="pill"
          className="w-full"
          onClick={() => router.push('/login')}
        >
          {t('forgotPassword.backToLogin')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <AuthBrandHeader title={t('forgotPassword.title')} />
      <p className="text-mindexia-muted -mt-2 text-center text-sm">{t('forgotPassword.intro')}</p>
      <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
        <Field
          id="email"
          label={t('fields.email')}
          srOnlyLabel
          error={fieldError(form.formState.errors.email?.message)}
        >
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder={t('forgotPassword.emailPlaceholder')}
            invalid={Boolean(form.formState.errors.email)}
            {...form.register('email')}
          />
        </Field>
        <Button
          type="submit"
          variant="mindexia"
          size="pill"
          className="mt-2 w-full"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? t('forgotPassword.submitLoading') : t('forgotPassword.submit')}
        </Button>
      </form>
      <Button
        type="button"
        variant="mindexiaOutline"
        size="pill"
        className="w-full"
        onClick={() => router.push('/login')}
      >
        {t('forgotPassword.backToLogin')}
      </Button>
    </div>
  );
}
